import React, {
  useRef,
  useState,
  HTMLAttributes,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useEffect
} from 'react'
import GoogleMapReact from 'google-map-react'
import useSupercluster from 'use-supercluster'

import { Marker } from './marker'
import { Cluster } from './cluster'

import styles from './styles.module.scss'
import { useFranchise } from 'context/franchise'
import { FranchiseDto } from '@services/api/franchise/types'

export interface MapRef {
  setZoom: (zoom: number) => void
  setPos: (lat: number, long: number) => void
}

interface MapProps extends HTMLAttributes<HTMLDivElement> {
  defaultZoom?: number
}

const Map: ForwardRefRenderFunction<MapRef, MapProps> = (
  { defaultZoom = 4, className, ...rest },
  ref
) => {
  const mapRef = useRef(null)
  const [bounds, setBounds] = useState(null)
  const [zoom, setZoom] = useState(defaultZoom)
  const [pointerZoom, setPointerZoom] = useState<FranchiseDto>()
  const { franchises, setFranchiseSelectedByMap, location } = useFranchise()

  useImperativeHandle(ref, () => ({
    setZoom: zoom => {
      mapRef.current?.setZoom(zoom)
    },
    setPos: (lat, lng) => {
      mapRef.current?.panTo({ lat, lng })
    }
  }))

  useEffect(() => {
    const _pointerZoom = location && franchises[0]
    setPointerZoom(_pointerZoom)
    const zoomCityOrState = location?.state ? 8 : 5.7
    setZoom(_pointerZoom ? zoomCityOrState : defaultZoom)
  }, [location])

  const { clusters, supercluster } = useSupercluster({
    points: franchises
      .filter(({ latitude, longitude }) => !!latitude && !!longitude)
      .map(franchise => ({
        type: 'Feature',
        properties: {
          cluster: false,
          id: franchise.id,
          name: franchise.district
        },
        geometry: {
          type: 'Point',
          coordinates: [franchise.longitude, franchise.latitude]
        }
      })),
    zoom,
    bounds,
    options: {
      radius: 110,
      maxZoom: 20
    }
  })

  return (
    <div className={styles.container + ' ' + className} {...rest}>
      {/* @ts-expect-error - google-map-react tem incompatibilidade de tipos com React 18 */}
      <GoogleMapReact
        options={{
          fullscreenControl: false,
          scrollwheel: true
        }}
        bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_API }}
        center={{
          lat: pointerZoom?.latitude || -14.103939,
          lng: pointerZoom?.longitude || -50.701436
        }}
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom)
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ])
        }}
        zoom={zoom}
      >
        {/* @ts-expect-error - google-map-react tem incompatibilidade de tipos com React 18 */}
        {clusters.map(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates
          const {
            id: franchiseId,
            cluster: isCluster,
            // @ts-expect-error - tipos de supercluster incompatíveis
            point_count: pointCount
          } = cluster.properties
          const clusterSize = `${35 + (pointCount / franchises.length) * 20}px`
          if (isCluster) {
            return (
              <Cluster
                key={`cluster_${cluster.id}`}
                lat={latitude}
                lng={longitude}
                width={clusterSize}
                height={clusterSize}
                size={pointCount}
                onClick={() => {
                  const expansionZoom = Math.min(
                    // @ts-expect-error - tipos de supercluster incompatíveis
                    supercluster.getClusterExpansionZoom(cluster.id),
                    20
                  )
                  mapRef.current.setZoom(expansionZoom)
                  mapRef.current.panTo({ lat: latitude, lng: longitude })
                }}
              />
            )
          }

          return (
            <Marker
              key={`franchise_${franchiseId}`}
              lat={latitude}
              lng={longitude}
              // @ts-expect-error - tipos de supercluster incompatíveis
              name={cluster.name}
              color="#ff9c00"
              onClick={() => {
                setFranchiseSelectedByMap &&
                  setFranchiseSelectedByMap(
                    franchises.find(({ id }) => franchiseId === id)
                  )
                if (zoom < 13) {
                  mapRef.current.setZoom(13)
                }
                mapRef.current.panTo({ lat: latitude, lng: longitude })
              }}
            />
          )
        })}
      </GoogleMapReact>
    </div>
  )
}

export const MapComponent = forwardRef(Map)
